<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { fromInputs, isAllDay, toDateInput, toTimeInput } from '@/todos/dates'

// Edits one backend temporal value: "YYYY-MM-DD" when no time is set (all-day), otherwise a
// datetime with the local offset. Emits only on a real change.
const { t } = useI18n()
const props = defineProps<{ modelValue: string | null; disabled?: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: string | null] }>()

const date = ref('')
const time = ref('')

watch(
  () => props.modelValue,
  (value) => {
    date.value = toDateInput(value)
    time.value = toTimeInput(value)
  },
  { immediate: true },
)

function commit(): void {
  const next = fromInputs(date.value, date.value ? time.value : '')
  if (next === props.modelValue) return
  if (
    next &&
    props.modelValue &&
    !isAllDay(next) &&
    !isAllDay(props.modelValue) &&
    Date.parse(next) === Date.parse(props.modelValue)
  )
    return
  emit('update:modelValue', next)
}

function clear(): void {
  date.value = ''
  time.value = ''
  commit()
}
</script>

<template>
  <div class="datetime">
    <input v-model="date" type="date" class="ui-input" :disabled="disabled" @change="commit" />
    <input
      v-model="time"
      type="time"
      class="ui-input time"
      :disabled="disabled || !date"
      :placeholder="t('datetime.allDay')"
      @change="commit"
    />
    <button
      v-if="modelValue && !disabled"
      type="button"
      class="ui-btn ui-btn-icon"
      :aria-label="t('common.clear')"
      @click="clear"
    >
      ✕
    </button>
  </div>
</template>

<style scoped>
.datetime {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}

.time {
  max-width: 7.5rem;
}
</style>
